import { createFrameTicker } from './recording-frame-ticker.util'
import { RECORDING_FRAME_RATE } from './recording.constant'
export type SplitVideoPaneRect = {
    x: number
    y: number
    width: number
    height: number
}

export type SplitVideoLayout = {
    canvasWidth: number
    canvasHeight: number
    screenPane: SplitVideoPaneRect
    cameraPane: SplitVideoPaneRect
}

const DEFAULT_WIDTH = 1280
const DEFAULT_HEIGHT = 720
const DEFAULT_FPS = RECORDING_FRAME_RATE

/** 50/50 horizontal split: screen left, camera right, equal width and full height. */
export function getSplitVideoLayout(canvasWidth = DEFAULT_WIDTH, canvasHeight = DEFAULT_HEIGHT): SplitVideoLayout {
    const halfWidth = canvasWidth / 2
    return {
        canvasWidth,
        canvasHeight,
        screenPane: { x: 0, y: 0, width: halfWidth, height: canvasHeight },
        cameraPane: { x: halfWidth, y: 0, width: halfWidth, height: canvasHeight },
    }
}

export type ContainedDrawRect = {
    x: number
    y: number
    width: number
    height: number
}

/** object-contain placement of source video inside a pane. */
export function getContainedDrawRect(sourceWidth: number, sourceHeight: number, pane: SplitVideoPaneRect): ContainedDrawRect | null {
    if (sourceWidth <= 0 || sourceHeight <= 0) {
        return null
    }
    const scale = Math.min(pane.width / sourceWidth, pane.height / sourceHeight)
    const width = sourceWidth * scale
    const height = sourceHeight * scale
    return {
        x: pane.x + (pane.width - width) / 2,
        y: pane.y + (pane.height - height) / 2,
        width,
        height,
    }
}

export type RecordingVideoSources = {
    cameraTrack: MediaStreamTrack | null
    screenTrack: MediaStreamTrack | null
}

/** split = screen + camera side by side; single = one source full frame; none = black frames. */
export type RecordingVideoLayoutMode = 'split' | 'single' | 'none'

function isLiveVideoTrack(track: MediaStreamTrack | null): track is MediaStreamTrack {
    return Boolean(track && track.kind === 'video' && track.readyState === 'live')
}

export function getRecordingVideoLayoutMode(sources: RecordingVideoSources): RecordingVideoLayoutMode {
    const cameraLive = isLiveVideoTrack(sources.cameraTrack)
    const screenLive = isLiveVideoTrack(sources.screenTrack)
    if (cameraLive && screenLive) {
        return 'split'
    }
    return cameraLive || screenLive ? 'single' : 'none'
}

export type RecordingVideoCompositorOptions = RecordingVideoSources & {
    width?: number
    height?: number
    fps?: number
}

export type RecordingVideoCompositor = {
    /** The one video track handed to MediaRecorder — the same object for the whole session. */
    getVideoTrack: () => MediaStreamTrack
    /** Point the canvas at new sources; returns true when the drawn sources changed. */
    setSources: (sources: RecordingVideoSources) => boolean
    stop: () => void
}

type BoundSource = {
    track: MediaStreamTrack
    video: HTMLVideoElement
}

function bindTrackToVideo(track: MediaStreamTrack): BoundSource {
    const video = document.createElement('video')
    video.muted = true
    video.playsInline = true
    video.autoplay = true
    video.srcObject = new MediaStream([track])
    video.play().catch(() => undefined)
    return { track, video }
}

function releaseSource(source: BoundSource | null) {
    if (!source) {
        return
    }
    source.video.pause()
    source.video.srcObject = null
}

/**
 * Canvas compositor that owns the recording's video track. Camera off/on,
 * camera switches and screen share only change what is drawn; the output
 * track never changes, so MediaRecorder keeps running and the video stream
 * never ends before the audio.
 */
export function createRecordingVideoCompositor(options: RecordingVideoCompositorOptions): RecordingVideoCompositor {
    const width = options.width ?? DEFAULT_WIDTH
    const height = options.height ?? DEFAULT_HEIGHT
    const fps = options.fps ?? DEFAULT_FPS
    const splitLayout = getSplitVideoLayout(width, height)
    const fullFrame: SplitVideoPaneRect = { x: 0, y: 0, width, height }

    const canvas = document.createElement('canvas')
    canvas.width = width
    canvas.height = height
    const ctx = canvas.getContext('2d')
    if (!ctx) {
        throw new Error('Failed to create 2D canvas context for recording video compositor')
    }

    let camera: BoundSource | null = null
    let screen: BoundSource | null = null
    let stopped = false

    const rebind = (current: BoundSource | null, track: MediaStreamTrack | null): BoundSource | null => {
        if (current?.track === track) {
            return current
        }
        releaseSource(current)
        return track ? bindTrackToVideo(track) : null
    }

    const drawPane = (source: BoundSource, pane: SplitVideoPaneRect) => {
        const rect = getContainedDrawRect(source.video.videoWidth, source.video.videoHeight, pane)
        if (!rect) {
            return
        }
        ctx.drawImage(source.video, rect.x, rect.y, rect.width, rect.height)
    }

    const drawFrame = () => {
        if (stopped) {
            return
        }
        ctx.fillStyle = '#000000'
        ctx.fillRect(0, 0, width, height)
        const sources = { cameraTrack: camera?.track ?? null, screenTrack: screen?.track ?? null }
        const mode = getRecordingVideoLayoutMode(sources)
        if (mode === 'split' && camera && screen) {
            drawPane(screen, splitLayout.screenPane)
            drawPane(camera, splitLayout.cameraPane)
        } else if (mode === 'single') {
            const single = isLiveVideoTrack(sources.screenTrack) ? screen : camera
            if (single) {
                drawPane(single, fullFrame)
            }
        }
    }

    const setSources = (sources: RecordingVideoSources): boolean => {
        if (stopped) {
            return false
        }
        const previousCamera = camera
        const previousScreen = screen
        camera = rebind(camera, sources.cameraTrack)
        screen = rebind(screen, sources.screenTrack)
        return camera !== previousCamera || screen !== previousScreen
    }

    const stream = canvas.captureStream(fps)
    setSources(options)
    drawFrame()
    // INTENTIONAL: a timer, not requestAnimationFrame — rAF stops entirely in a hidden
    // tab, which would end the recorded video stream early (the HLS transcode then
    // drops all audio after the last video frame). The timer runs in a Web Worker:
    // a hidden tab's main-thread timers slow to ~1/s (a slideshow recording), worker
    // timers keep the frame rate. Falls back to setInterval where workers aren't available.
    const ticker = createFrameTicker(fps, drawFrame)

    return {
        getVideoTrack: () => {
            const track = stream.getVideoTracks()[0]
            if (!track) {
                throw new Error('Recording video compositor produced no video track')
            }
            return track
        },
        setSources,
        stop() {
            if (stopped) {
                return
            }
            stopped = true
            ticker.stop()
            releaseSource(camera)
            releaseSource(screen)
            camera = null
            screen = null
            stream.getTracks().forEach((track) => track.stop())
        },
    }
}
